import React, { useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import useHomeworkReportView from "features/homework/hooks/useHomeworkReportView";
import PLAY_SVG from "assets/images/play.svg";
import Calendar from "./Calendar";
import Modal from "shared/components/Modal";
import { Button } from "shared/components/Button";
import { report } from "shared/types/Homework";

interface HomeworkLessonView {
    selectedLesson: string;
    onReportClickHandler: (report: report) => void
}

interface FolderPathProps {
    inDrawer?: boolean;
}

const HomeworkReportView = ({selectedLesson, onReportClickHandler}:HomeworkLessonView) => {
    const [selectedMonth, setSelectedMonth] = useState<Date>(new Date());
    const [viewType, setViewType] = useState<"list" | "calendar">("calendar");
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const {reportList, isOpenLoadingModal, loadingProgress, deleteReport, loadError, retryLoad} = useHomeworkReportView(selectedLesson, selectedMonth);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    const [selectedReportId, setSelectedReportId] = useState<string>("");
    const [searchKeyword, setSearchKeyword] = useState("");

    // 검색어로 필터링된 과제 목록
    const filteredBySearch = reportList.filter(report => 
        report.title.toLowerCase().includes(searchKeyword.toLowerCase())
    );

    // 선택된 달의 과제만 필터링
    const filteredReportList = reportList.filter(report => {
        const reportDate = report.date;
        return reportDate &&
               reportDate.getMonth() === selectedMonth.getMonth() && 
               reportDate.getFullYear() === selectedMonth.getFullYear();
    }).sort((a, b) => {
        // 날짜로 먼저 정렬
        const dateCompare = a.date.getTime() - b.date.getTime();
        if (dateCompare !== 0) return dateCompare;
        // 같은 날짜인 경우 이름순 정렬
        return a.title.localeCompare(b.title, 'ko');
    });

    // 날짜별로 과제 매핑 (Calendar 컴포넌트용)
    const eventsByDate = filteredReportList.reduce((acc, report) => {
        const dateStr = report.date.toISOString().split('T')[0];
        if (!acc[dateStr]) {
            acc[dateStr] = [];
        }
        acc[dateStr].push(report);
        // 같은 날짜 내에서 이름순 정렬
        acc[dateStr].sort((a, b) => a.title.localeCompare(b.title, 'ko'));
        return acc;
    }, {} as Record<string, typeof filteredReportList>);

    const handleMonthChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const [year, month] = event.target.value.split('-').map(Number);
        const newDate = new Date(year, month - 1);
        setSelectedMonth(newDate);
        setSelectedDate(null);
    };

    const handleDeleteClick = (release_id: string) => {
        setSelectedReportId(release_id);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        await deleteReport(selectedReportId);
        setIsDeleteModalOpen(false);
        setIsSuccessModalOpen(true);
    };

    const getReportsForSelectedDay = () => {
        if (!selectedDate) return [];
        const dateStr = selectedDate.toISOString().split('T')[0];
        return eventsByDate[dateStr] || [];
    };

    return(
        <Container>
            <CustomCalendarStyle />
            <HeaderContainer>
                <TabWrapper>
                    <TabButton active={viewType === "list"} onClick={() => setViewType("list")}>
                        리스트로 보기
                    </TabButton>
                    <TabButton active={viewType === "calendar"} onClick={() => setViewType("calendar")}>
                        캘린더로 보기
                    </TabButton>
                </TabWrapper>
                <ControlsContainer>
                    {viewType === "list" && (
                        <SearchContainer>
                            <SearchInput
                                type="text"
                                placeholder="과제명으로 검색..."
                                value={searchKeyword}
                                onChange={(e) => setSearchKeyword(e.target.value)}
                            />
                        </SearchContainer>
                    )}
                    <MonthSelector value={`${selectedMonth.getFullYear()}-${String(selectedMonth.getMonth() + 1).padStart(2, '0')}`} onChange={handleMonthChange}>
                        {Array.from({length: 13}).map((_, i) => {
                            const currentDate = new Date();
                            const startYear = currentDate.getFullYear();
                            const startMonth = currentDate.getMonth(); // 0-based month
                            
                            // 이전 6개월부터 다음 6개월까지 (총 13개월)
                            const offset = i - 6; // -6부터 +6까지
                            const targetDate = new Date(startYear, startMonth + offset, 1);
                            const year = targetDate.getFullYear();
                            const month = targetDate.getMonth() + 1; // 1-based month
                            
                            return (
                                <option key={`${year}-${month}`} value={`${year}-${String(month).padStart(2, '0')}`}>
                                    {year}년 {month}월
                                </option>
                            );
                        })}
                    </MonthSelector>
                </ControlsContainer>
            </HeaderContainer>

            {isOpenLoadingModal ? (
                <LoadingContainer>
                    <LoadingSpinner />
                    <LoadingText>과제 목록을 불러오는 중...</LoadingText>
                </LoadingContainer>
            ) : loadError ? (
                <LoadingContainer role="alert">
                    <LoadingText>{loadError}</LoadingText>
                    <Button name="다시 시도" onButtonClick={retryLoad} />
                </LoadingContainer>
            ) : viewType === "list" ? (
                <Table>
                    <TableHeader>
                        <TableColumn style={{width:"25%"}}>과제명</TableColumn>
                        <TableColumn style={{width:"35%"}}>날짜</TableColumn>
                        <TableColumn style={{width:"20%"}}>자세히 보기</TableColumn>
                        <TableColumn style={{width:"20%"}}>삭제</TableColumn>
                    </TableHeader>
                    <TableBody>
                        {filteredReportList.length === 0 ? (
                            <EmptyContainer>
                                <EmptyText>
                                    {searchKeyword 
                                        ? `"${searchKeyword}" 검색 결과가 없습니다` 
                                        : `${selectedMonth.getFullYear()}년 ${selectedMonth.getMonth() + 1}월 과제가 없습니다`
                                    }
                                </EmptyText>
                            </EmptyContainer>
                        ) : (
                            filteredReportList.map((item) => (
                                <TableRow key={item.release_id}>
                                    <FolderPath inDrawer={false}>
                                        {item.folderPath.map((folder, index) => (
                                            <React.Fragment key={index}>
                                                {index > 0 && <FolderSeparator>/</FolderSeparator>}
                                                {folder}
                                            </React.Fragment>
                                        ))}
                                    </FolderPath>
                                    <RowContent>
                                        <TableColumn style={{width:"25%"}}>{item.title}</TableColumn>
                                        <TableColumn style={{width:"35%"}}>{item.date.toLocaleDateString()}</TableColumn>
                                        <TableColumn style={{width:"20%"}}>
                                            <DetailButton 
                                                src={PLAY_SVG} 
                                                alt="detail button" 
                                                onClick={() => onReportClickHandler(item)}
                                            />
                                        </TableColumn>
                                        <TableColumn style={{width:"20%"}}>
                                            <DeleteButton onClick={() => handleDeleteClick(item.release_id)}>
                                                삭제
                                            </DeleteButton>
                                        </TableColumn>
                                    </RowContent>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            ) : (
                <CalendarWrapper>
                    <Calendar
                        selectedDate={selectedDate}
                        onDateSelect={setSelectedDate}
                        events={eventsByDate}
                        activeStartDate={selectedMonth}
                    />
                    {selectedDate && (
                        <DrawerContainer onClick={() => setSelectedDate(null)}>
                            <DrawerContent onClick={(e) => e.stopPropagation()}>
                                <DrawerHeader>
                                    <DrawerTitle>
                                        {selectedDate.getFullYear()}년 {selectedDate.getMonth() + 1}월 {selectedDate.getDate()}일 과제
                                    </DrawerTitle>
                                    <CloseButton onClick={() => setSelectedDate(null)}>×</CloseButton>
                                </DrawerHeader>
                                <DrawerBody>
                                    {getReportsForSelectedDay().length === 0 ? (
                                        <EmptyDrawerText>해당 날짜에 과제가 없습니다</EmptyDrawerText>
                                    ) : (
                                        <DrawerList>
                                            {getReportsForSelectedDay().map((item) => (
                                                <DrawerItem key={item.release_id}>
                                                    <DrawerItemContent>
                                                        <FolderPath inDrawer={true}>
                                                            {item.folderPath.map((folder, index) => (
                                                                <React.Fragment key={index}>
                                                                    {index > 0 && <FolderSeparator>/</FolderSeparator>}
                                                                    {folder}
                                                                </React.Fragment>
                                                            ))}
                                                        </FolderPath>
                                                        <DrawerItemTitle>{item.title}</DrawerItemTitle>
                                                    </DrawerItemContent>
                                                    <DrawerItemActions>
                                                        <DetailButton
                                                            src={PLAY_SVG}
                                                            alt="detail button"
                                                            onClick={() => onReportClickHandler(item)}
                                                        />
                                                        <DeleteButton onClick={() => handleDeleteClick(item.release_id)}>
                                                            삭제
                                                        </DeleteButton>
                                                    </DrawerItemActions>
                                                </DrawerItem>
                                            ))}
                                        </DrawerList>
                                    )}
                                </DrawerBody>
                            </DrawerContent>
                        </DrawerContainer>
                    )}
                </CalendarWrapper>
            )}

            <Modal
                title="과제 삭제"
                content="정말로 이 과제를 삭제하시겠습니까?"
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
            />

            <Modal
                title="삭제 완료"
                content="과제가 성공적으로 삭제되었습니다."
                isOpen={isSuccessModalOpen}
                onClose={() => setIsSuccessModalOpen(false)}
            />
        </Container>
    )
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    padding: 20px;
    background: #FFFFFF;
    border: 1px solid #E2E8F0;
    border-radius: 16px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05), 0 8px 24px rgba(15, 23, 42, 0.04);
    min-height: 0;
`

const LoadingSpinner = styled.div`
    width: 40px;
    height: 40px;
    border: 3px solid #E2E8F0;
    border-top: 3px solid #DF0000;
    border-radius: 50%;
    animation: spin 1s linear infinite;

    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
`

const Table = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    flex-grow: 1;
    animation: fadeInUp 0.5s ease-in-out;
    background: #FFFFFF;
    border-radius: 8px;
    overflow: hidden;
`

const TableHeader = styled.div`
    display: flex;
    background: #F8FAFC;
    border-bottom: 2px solid #E2E8F0;
    position: sticky;
    top: 0;
    z-index: 1;
`

const TableColumn = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 8px 16px;
    font-size: 14px;
    color: #64748B;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const TableBody = styled.div`
    flex: 1;
    min-height: 0;
    overflow: auto;
    
    &::-webkit-scrollbar {
        width: 6px;
    }
    
    &::-webkit-scrollbar-track {
        background: #F1F5F9;
    }
    
    &::-webkit-scrollbar-thumb {
        background: #CBD5E1;
        border-radius: 3px;
    }
`

const TableRow = styled.div<{selected?:boolean}>`
    display: flex;
    flex-direction: column;
    border-bottom: 1px solid #E2E8F0;
    background-color: ${(props) => props.selected ? "#FEF2F2" : "#FFFFFF"};
    transition: all 0.2s ease;
    
    &:hover {
        background-color: #F8FAFC;
    }
`

const FolderPath = styled.div<FolderPathProps>`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: ${props => props.inDrawer ? '4px 0px' : '4px 16px'};
    font-size: 12px;
    color: #64748B;
`

const FolderSeparator = styled.span`
    color: #94A3B8;
`

const RowContent = styled.div`
    display: flex;
    width: 100%;
`

const DetailButton = styled.img`
    width: 30px;
    height: 30px;
    padding: 4px;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
        background-color: #F1F5F9;
        transform: scale(1.1);
    }
    
    &:active {
        transform: scale(0.95);
    }
`

const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 16px;
`

const LoadingText = styled.div`
    color: #64748B;
    font-size: 14px;
    font-weight: 500;
`

const EmptyContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
`

const EmptyText = styled.div`
    color: #64748B;
    font-size: 14px;
    font-weight: 500;
`

const SortButton = styled.button`
    display: flex;
    align-items: center;
    gap: 4px;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    color: #64748B;
    font-size: 14px;
    font-weight: 600;
    transition: color 0.2s ease;

    &:hover {
        color: #DF0000;
    }
`

const SortIcon = styled.span<{sortOrder: 'asc' | 'desc'}>`
    font-size: 10px;
    transform: ${props => props.sortOrder === 'desc' ? 'rotate(180deg)' : 'none'};
    transition: transform 0.2s ease;
`

const DeleteButton = styled.button`
    padding: 6px 12px;
    background-color: #EF4444;
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background-color: #DC2626;
    }

    &:active {
        transform: scale(0.95);
    }
`

const HeaderContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
`

const TabWrapper = styled.div`
    display: flex;
    gap: 10px;
`

const TabButton = styled.button<{active: boolean}>`
    padding: 10px 20px;
    border: none;
    border-radius: 8px;
    background: ${props => props.active ? '#DF0000' : '#F8FAFC'};
    color: ${props => props.active ? '#FFFFFF' : '#64748B'};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: ${props => props.active ? '#B91C1C' : '#F1F5F9'};
    }
`

const ControlsContainer = styled.div`
    display: flex;
    gap: 12px;
    align-items: center;
`

const SearchContainer = styled.div`
    position: relative;
`

const SearchInput = styled.input`
    padding: 8px 12px;
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    background: #FFFFFF;
    color: #1E293B;
    font-size: 14px;
    width: 200px;
    outline: none;

    &:focus {
        border-color: #DF0000;
        box-shadow: 0 0 0 3px rgba(223, 0, 0, 0.12);
    }

    &::placeholder {
        color: #94A3B8;
    }
`

const MonthSelector = styled.select`
    padding: 8px 12px;
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    background: #FFFFFF;
    color: #1E293B;
    font-size: 14px;
    cursor: pointer;
    outline: none;

    &:focus {
        border-color: #DF0000;
        box-shadow: 0 0 0 3px rgba(223, 0, 0, 0.12);
    }
`

const CustomCalendarStyle = createGlobalStyle`
    .react-calendar {
        border: none;
        border-radius: 16px;
        box-shadow: 0 2px 16px rgba(0,0,0,0.08);
        font-family: 'Pretendard', 'Noto Sans KR', sans-serif;
        background: #fff;
        padding: 16px 8px 8px 8px;
    }
    .react-calendar__navigation {
        display: flex;
        justify-content: center;
        margin-bottom: 8px;
    }
    .react-calendar__navigation button {
        min-width: 36px;
        background: none;
        border: none;
        color: #DF0000;
        font-size: 1.1rem;
        font-weight: 600;
        margin: 0 2px;
        border-radius: 6px;
        transition: background 0.2s;
    }
    .react-calendar__navigation button:enabled:hover {
        background: #f1f5f9;
    }
    .react-calendar__month-view__weekdays {
        text-align: center;
        font-weight: 700;
        color: #64748B;
        font-size: 1rem;
        margin-bottom: 4px;
    }
    .react-calendar__month-view__weekdays__weekday {
        padding: 6px 0;
    }
    .react-calendar__tile {
        background: none;
        border: none;
        border-radius: 8px;
        padding: 8px 0 6px 0;
        font-size: 1rem;
        color: #334155;
        transition: background 0.2s, color 0.2s;
        position: relative;
    }
    .react-calendar__tile:enabled:hover,
    .react-calendar__tile:enabled:focus {
        background: #e0e7ef;
        color: #DF0000;
    }
    .react-calendar__tile--active {
        background: #DF0000;
        color: #fff;
        font-weight: 700;
    }
    .react-calendar__tile--now {
        border: 1.5px solid #DF0000;
        background: #FEF2F2;
        color: #DF0000;
    }
    .calendar-dot {
        display: block;
        margin: 2px auto 0 auto;
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: #DF0000;
    }
`

const CalendarWrapper = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    height: 100%;
    width: 100%;
`

const DrawerContainer = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`

const DrawerContent = styled.div`
    background: #FFFFFF;
    border-radius: 12px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
    width: 90%;
    max-width: 600px;
    max-height: 80vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`

const DrawerHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    background: #F8FAFC;
    border-bottom: 1px solid #E2E8F0;
`

const DrawerTitle = styled.h3`
    font-size: 18px;
    font-weight: 600;
    color: #1E293B;
    margin: 0;
`

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 24px;
    font-weight: 400;
    cursor: pointer;
    color: #64748B;
    padding: 4px;
    border-radius: 4px;
    transition: all 0.2s ease;

    &:hover {
        background: #E2E8F0;
        color: #1E293B;
    }
`

const DrawerBody = styled.div`
    padding: 24px;
    overflow-y: auto;
    flex-grow: 1;
`

const EmptyDrawerText = styled.div`
    color: #64748B;
    font-size: 16px;
    font-weight: 500;
    text-align: center;
    padding: 40px 20px;
`

const DrawerList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`

const DrawerItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    background: #F8FAFC;
    border-radius: 8px;
    border: 1px solid #E2E8F0;
    transition: all 0.2s ease;

    &:hover {
        background: #F1F5F9;
        border-color: #CBD5E1;
    }
`

const DrawerItemContent = styled.div`
    flex: 1;
    margin-right: 16px;
`

const DrawerItemTitle = styled.h4`
    font-size: 16px;
    font-weight: 600;
    color: #1E293B;
    margin: 0 0 8px 0;
`

const DrawerItemProgress = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`

const ProgressBar = styled.div<{progress: number}>`
    flex-grow: 1;
    height: 8px;
    background: #E2E8F0;
    border-radius: 4px;
    overflow: hidden;
    position: relative;

    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: ${props => props.progress}%;
        background: ${props => {
            if (props.progress >= 80) return '#10B981';
            if (props.progress >= 40) return '#F97316';
            return '#F59E0B';
        }};
        border-radius: 4px;
        transition: width 0.3s ease, background-color 0.3s ease;
    }
`

const ProgressText = styled.span`
    font-size: 14px;
    color: #64748B;
    font-weight: 500;
    min-width: 40px;
    text-align: right;
`

const DrawerItemActions = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
`

export default HomeworkReportView;
